import {Roles} from '@hconnect/apiclient'
import {useUserContext} from '@hconnect/login'
import {useCallback} from 'react'
import {useSearchParams} from 'react-router'

import {PlantStructureFilterOptions} from '../../../common/enums'

const PLANT_SETUP_FILTER_KEY = 'plantSetupFilter'

const getRoleBasedDefaultFilter = (userRoles?: string[]): PlantStructureFilterOptions => {
  if (!userRoles) return PlantStructureFilterOptions.All

  const hasJanusRoles =
    userRoles.includes(Roles.HproducePlantController) ||
    userRoles.includes(Roles.HproduceCCCSupporter)

  return hasJanusRoles ? PlantStructureFilterOptions.Janus : PlantStructureFilterOptions.All
}

const getStoredFilter = (userId?: string): PlantStructureFilterOptions | null => {
  if (!userId) return null

  try {
    const key = `${PLANT_SETUP_FILTER_KEY}_${userId}`
    const stored = localStorage.getItem(key)
    return stored as PlantStructureFilterOptions | null
  } catch {
    return null
  }
}

const setStoredFilter = (userId: string | undefined, filter: PlantStructureFilterOptions) => {
  if (!userId) return

  try {
    const key = `${PLANT_SETUP_FILTER_KEY}_${userId}`
    localStorage.setItem(key, filter)
  } catch (error) {
    console.warn('Failed to save filter to local storage:', error)
  }
}

export const usePlantSetupFilter = () => {
  const {loggedUser} = useUserContext()
  const [searchParams, setSearchParams] = useSearchParams()

  const userId = loggedUser?.user_id
  const roleBasedDefault = getRoleBasedDefaultFilter(loggedUser?.hasRoles)
  const urlFilter = searchParams.get('filter')

  const storedFilter = userId ? getStoredFilter(userId) : null
  const selectedFilterOption =
    (urlFilter as PlantStructureFilterOptions) || storedFilter || roleBasedDefault

  const handleSetFilter = useCallback(
    (filter: PlantStructureFilterOptions) => {
      if (userId) {
        setStoredFilter(userId, filter)
      }

      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev)
          if (filter === PlantStructureFilterOptions.All) {
            newParams.delete('filter')
          } else {
            newParams.set('filter', filter)
          }
          return newParams
        },
        {replace: true}
      )
    },
    [setSearchParams, userId]
  )

  return {
    selectedFilterOption,
    handleSetFilter
  }
}
